/**
 * Handles user actions on a checklist editor.
 */
class ChecklistEditor {
  constructor(editor, notifyOfListItemsChanged) {
    this.editor = editor;
    this.notifyOfListItemsChanged = notifyOfListItemsChanged;

    this.editor.addEventListener('dragenter', ev => this.dragEnter(ev));
    this.editor.addEventListener('dragend', () => this.dragEnd());
    this.editor.addEventListener('keydown', ev => this.processKey(ev));
    this.editor.addEventListener('input', () => this.editorChanged());
    this.editor.addEventListener('click', ev => this.adjustSelection(ev));

    this.insertionPointNode = null;
    this.dragoverTarget = null;
  }

  processKey(ev) {
    switch (ev.key) {
      case 'Tab':
        this.processTabOrShiftTabKey(ev);
        break;

      case 'Enter':
        this.processEnterKey(ev);
        break;

      case 'Backspace':
        this.processBackspace(ev);
        break;

      default:
        break;
    }
  }

  processTabOrShiftTabKey(ev) {
    let focusNode = window.getSelection().focusNode;
    let focusOffset = window.getSelection().focusOffset;

    let li = focusNode;
    while (li && li.localName != 'li') {
      li = li.parentNode;
    }
    if (!li) {
      return;
    }

    ev.preventDefault();
    if (ev.shiftKey) {
      this.processShiftTabKey(focusNode, focusOffset, li);
    } else {
      this.processTabKey(focusNode, focusOffset, li);
    }
  }

  processShiftTabKey(focusNode, focusOffset, li) {
    let nextSibling = li.nextElementSibling;

    let ul = li.parentNode;
    let parentLi = ul.previousElementSibling;
    if (!parentLi || parentLi.localName != 'li') {
      return;
    }

    let sublist = null;

    // All siblings (if any) go into li's sublist.
    if (nextSibling) {
      sublist = nextSibling.localName == 'ul' ? nextSibling : null;

      let selrange = document.createRange();
      if (sublist) {
        selrange.setStartAfter(sublist);
      } else {
        selrange.setStartBefore(nextSibling);
      }
      selrange.setEndAfter(li.parentNode.lastElementChild);

      if (!selrange.collapsed) {
        let siblings = selrange.extractContents();
        let insrange = document.createRange();

        if (!sublist) {
          sublist = li.parentNode.insertBefore(
            document.createElement('ul'),
            li.nextElementSibling
          );
        }
        insrange.setStart(sublist, sublist.childElementCount);
        insrange.insertNode(siblings);
      }
    }

    // li itself goes after ul the list is contained in or into a new top
    // level ul if it becomes a top level item.

    let selrange = document.createRange();
    selrange.setStartBefore(li);
    selrange.setEndAfter(sublist || li);

    let insrange = document.createRange();

    // Top-level items have own ul.
    let newUl = ul;
    let grandParentUl = parentLi.parentNode;
    if (grandParentUl.parentNode == this.editor) {
      newUl = document.createElement('ul');
      this.editor.insertBefore(newUl, grandParentUl.nextElementSibling);
      insrange.setStart(newUl, 0);
    } else {
      insrange.setStartAfter(newUl);
    }
    insrange.insertNode(selrange.extractContents());

    if (!ul.firstChild) {
      ul.parentNode.removeChild(ul);
    }

    window.getSelection().collapse(focusNode, focusOffset);
    this.notifyOfListItemsChanged();
  }

  processTabKey(focusNode, focusOffset, li) {
    let prevSibling = li.previousElementSibling;
    if (!prevSibling) {
      return;
    }
    let nextSibling = li.nextElementSibling;

    // Tab
    if (prevSibling.localName == 'li') {
      if (nextSibling && nextSibling.localName == 'ul') {
        // #2: tabbed item already has a sublist
        // food
        //   potato
        //   onion
        //   tomato - tab moves |tomato| under |onion|, aligned with |cucumber|
        //     cucumber
        nextSibling.insertBefore(li, nextSibling.firstElementChild);
      } else {
        // #1: wrap content by ul and make it a child of li.
        // food
        //   potato
        //   onion (tab moves |onion| under potato)
        let range = document.createRange();
        range.selectNode(li);
        let sublist = document.createElement('ul');
        range.surroundContents(sublist);
      }
    } else if (prevSibling.localName == 'ul') {
      // #3: tabbed item goes into a previous child sublist
      // food
      //   potato
      //    onion
      //   tomato - tab moves |tomato| under |potato|, aligned with |onion|
      prevSibling.appendChild(li);

      if (nextSibling && nextSibling.localName == 'ul') {
        // #3.1: tabbed item goes into a previous child sublist within its subtree
        // food
        //   potato
        //    onion
        //   tomato - tab moves |tomato| under |potato|, aligned with |onion|
        //     potato
        while (nextSibling.firstElementChild) {
          prevSibling.appendChild(nextSibling.firstElementChild);
        }
        nextSibling.remove();
      }
    }

    window.getSelection().collapse(focusNode, focusOffset);
    this.notifyOfListItemsChanged();
  }

  processEnterKey(ev) {
    let focusNode = window.getSelection().focusNode;
    let focusOffset = window.getSelection().focusOffset;

    // #1: Enter on top level list item inserts a sublist.
    if (focusNode.parentNode.localName == 'li') {
      let li = focusNode.parentNode;
      let ul = li.parentNode;
      // top level UL having a single LI child, containing a focus node.
      if (this.#isRoot(ul.parentNode) && ul.childElementCount == 1) {
        // top UL
        let movedText = li.textContent.substring(focusOffset);
        li.textContent = li.textContent.substring(0, focusOffset);

        let nestedUl = document.createElement('ul');
        nestedUl.innerHTML = `<li>${movedText}</li>`;
        ul.appendChild(nestedUl);

        this.setSelectionAt(nestedUl.firstElementChild);

        ev.preventDefault();
        this.notifyOfListItemsChanged();
        return;
      }
    } else if (
      this.#isRoot(focusNode) &&
      focusOffset == focusNode.childElementCount
    ) {
      // b) On Windows Chrome under Selenium pressing enter key on top level
      // list item moves the selection focus at the end of the editor (selection
      // have changed when keydown event is handled). Apparently, this is Chrome
      // bug, which only happens when Chrome is controlled by automation
      // software. The behavior breaks a number of tests, so let's keep the hack
      // here for now. However the code should be revisited once is a while,
      //in case if behavior changes.
      let ul = focusNode.lastElementChild;
      if (ul.childElementCount == 1) {
        let nestedUl = document.createElement('ul');
        nestedUl.innerHTML = `<li></li>`;
        ul.appendChild(nestedUl);

        this.setSelectionAt(nestedUl.firstElementChild);

        ev.preventDefault();
        this.notifyOfListItemsChanged();
        return;
      }
    }

    // #2: Consequent enter inserts a new top level list. Note, Windows Chrome
    // under Selenium hits the case of selection at the end of the editor.
    // It affects a single test test only, so let's keep it failing. See above.
    if (focusNode.localName == 'li' && focusNode.textContent == '') {
      let ul = focusNode.parentNode;
      let topUl = ul.parentNode;
      if (topUl.localName == 'ul' && this.#isRoot(topUl.parentNode)) {
        let newUl = document.createElement('ul');
        newUl.appendChild(focusNode);
        topUl.parentNode.insertBefore(newUl, topUl.nextElementSibling);
        this.setSelectionAt(focusNode);

        ev.preventDefault();
        this.notifyOfListItemsChanged();
        return;
      }
    }

    // #3: If the cursor is at a list item that has a sub list, and the user hits
    // enter to insert a new item, then insert a new item into the sub list as
    // a first child.
    let li = focusNode.parentNode;
    if (li.localName == 'li') {
      let sublist = li.nextElementSibling;
      if (sublist && sublist.localName == 'ul') {
        let txt = focusNode.splitText(focusOffset);
        let li = document.createElement('li');
        li.appendChild(txt);
        sublist.insertBefore(li, sublist.firstChild);
        this.setSelectionAt(li);

        ev.preventDefault();
        this.notifyOfListItemsChanged();
      }
    }

    // #4: default case
  }

  processBackspace(ev) {
    // Note, it may not work on Firefox mobile, see more at
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1104817.
    let editor = ev.target;
    window.setTimeout(() => {
      // Move unparented list under the current selection.
      let ul = editor.querySelector('ul > ul:only-child');
      if (ul) {
        let focusNode = window.getSelection().focusNode;
        let li = focusNode.parentNode;
        if (li.localName == 'li') {
          let deadUl = ul.parentNode;
          li.parentNode.insertBefore(ul, li.nextElementSibling);
          deadUl.parentNode.removeChild(deadUl);
        }
      }
    }, 0);
  }

  adjustSelection() {
    // selection may stuck on ui element preventing editing when user click on
    // a checklist, adjust selection to place it on element.
    let focusNode = window.getSelection().focusNode;
    let el = focusNode;
    while (el && el != this.editor) {
      el = el.parentNode;
    }

    if (
      el &&
      focusNode.localName == 'ul' &&
      focusNode.firstElementChild &&
      focusNode.firstElementChild.localName == 'li'
    ) {
      let sel = window.getSelection();
      let r = sel.getRangeAt(0);
      r.setStart(focusNode.firstElementChild, 0);
      r.setEnd(focusNode.firstElementChild, 0);
      sel.removeAllRanges();
      sel.addRange(r);
    }
  }

  setSelectionAt(el) {
    let sel = window.getSelection();
    let r = sel.getRangeAt(0);
    r.setStart(el, 0);
    r.setEnd(el, 0);
    sel.removeAllRanges();
    sel.addRange(r);
  }

  editorChanged() {
    if (!this.editor.isContentEditable) {
      return;
    }

    let focusNode = window.getSelection().focusNode;
    if (!focusNode || focusNode.nodeType != Node.TEXT_NODE) {
      return;
    }

    let nextSibling = focusNode.nextElementSibling;

    // Firefox: remove trailing <br> if text node is not empty. Do this in
    // timeout, otherwise it also removes trailing whitespaces.
    window.setTimeout(() => {
      if (
        focusNode.wholeText != '' &&
        nextSibling &&
        nextSibling.localName == 'br'
      ) {
        nextSibling.remove();
      }
    }, 0);

    // If text node is out of list, then move it to the closest list or create
    // a new one if nothing is found.
    if (focusNode.parentNode && focusNode.parentNode.localName != 'li') {
      let newul =
        nextSibling && nextSibling.localName == 'ul'
          ? nextSibling
          : focusNode.parentNode.insertBefore(
              document.createElement('ul'),
              focusNode
            );

      let newli =
        newul.firstElementChild && newul.firstElementChild.localName == 'li'
          ? newul.firstElementChild
          : newul.appendChild(document.createElement('li'));

      newli.appendChild(focusNode);

      // Remove trailing <br> if any.
      if (this.editor.lastElementChild.localName == 'br') {
        this.editor.lastElementChild.remove();
      }

      let sel = window.getSelection();
      let r = sel.getRangeAt(0);
      r.setStart(focusNode, focusNode.textContent.length);
      r.setEnd(focusNode, focusNode.textContent.length);
      sel.removeAllRanges();
      sel.addRange(r);
    }

    this.notifyOfListItemsChanged();
  }

  dragEnter(aEvent) {
    if (!this.insertionPointNode) {
      this.insertionPointNode = document.createElement('li');
      this.insertionPointNode.className = 'insertionpoint';
    }

    let target =
      aEvent.target.nodeType == Node.TEXT_NODE
        ? aEvent.target.parentNode
        : aEvent.target;
    if (target == this.insertionPointNode) {
      return;
    }

    if (this.dragoverTarget != target) {
      if (this.dragoverTarget) {
        this.dragoverTarget.className = '';
      }

      this.dragoverTarget = target;
      this.dragoverTarget.className = 'droptarget';

      if (this.dragoverTarget.localName == 'li') {
        this.dragoverTarget.parentNode.insertBefore(
          this.insertionPointNode,
          this.dragoverTarget.nextSibling
        );
      }
    }
  }

  dragEnd() {
    if (this.insertionPointNode.parentNode) {
      this.insertionPointNode.remove();
    }
  }

  #isRoot(el) {
    return el.localName == 'checklist-view-edit';
  }
}

export { ChecklistEditor };
